/**
 * Módulo de familias - Dominio compartido
 *
 * Contiene casos de uso y lógica de negocio para gestión de familias,
 * independiente del framework de UI.
 */

export * from './avatars'

import { FamilyApiService, MemberApiService } from '@pepi/api-client'
import type {
  Family,
  CreateFamilyRequest,
  UpdateFamilyRequest,
  FamilyMember,
  CreateMemberRequest,
  UpdateMemberRequest,
  FamilyRole
} from '@pepi/api-client'

// ============== TIPOS ==============

export interface FamilyOperationResult<T> {
  success: boolean
  data?: T
  error?: string
}

// ============== CASOS DE USO - FAMILIAS ==============

/**
 * Caso de uso: Cargar todas las familias del usuario
 */
export async function loadFamiliesUseCase(): Promise<Family[]> {
  return await FamilyApiService.getFamilies()
}

/**
 * Caso de uso: Cargar una familia por ID
 */
export async function loadFamilyUseCase(familyId: number): Promise<Family> {
  return await FamilyApiService.getFamilyById(familyId)
}

/**
 * Caso de uso: Crear una nueva familia
 */
export async function createFamilyUseCase(data: CreateFamilyRequest): Promise<Family> {
  // Validar datos de entrada
  validateFamilyData(data)
  return await FamilyApiService.createFamily(data)
}

/**
 * Caso de uso: Actualizar una familia existente
 */
export async function updateFamilyUseCase(familyId: number, data: UpdateFamilyRequest): Promise<Family> {
  // Validar datos de entrada
  if (data.name !== undefined) {
    validateFamilyName(data.name)
  }
  return await FamilyApiService.updateFamily(familyId, data)
}

/**
 * Caso de uso: Eliminar una familia
 */
export async function deleteFamilyUseCase(familyId: number): Promise<void> {
  return await FamilyApiService.deleteFamily(familyId)
}

/**
 * Caso de uso: Cargar roles disponibles
 */
export async function loadFamilyRolesUseCase(): Promise<FamilyRole[]> {
  return await FamilyApiService.getFamilyRoles()
}

// ============== CASOS DE USO - MIEMBROS ==============

/**
 * Caso de uso: Cargar miembros de una familia
 */
export async function loadFamilyMembersUseCase(familyId: number): Promise<FamilyMember[]> {
  return await MemberApiService.getFamilyMembers(familyId)
}

/**
 * Caso de uso: Cargar un miembro por ID
 */
export async function loadMemberUseCase(memberId: number): Promise<FamilyMember> {
  return await MemberApiService.getMemberById(memberId)
}

/**
 * Caso de uso: Crear un nuevo miembro en la familia
 */
export async function createMemberUseCase(familyId: number, data: CreateMemberRequest): Promise<FamilyMember> {
  // Validar datos de entrada
  validateMemberData(data)
  return await MemberApiService.createFamilyMember(familyId, data)
}

/**
 * Caso de uso: Actualizar un miembro existente
 */
export async function updateMemberUseCase(memberId: number, data: UpdateMemberRequest): Promise<FamilyMember> {
  // Validar datos si hay firstName
  if (data.firstName !== undefined) {
    validateMemberFirstName(data.firstName)
  }
  return await MemberApiService.updateMember(memberId, data)
}

/**
 * Caso de uso: Eliminar un miembro
 */
export async function deleteMemberUseCase(memberId: number): Promise<void> {
  return await MemberApiService.deleteMember(memberId)
}

// ============== VALIDADORES ==============

/**
 * Valida los datos para crear una familia
 */
export function validateFamilyData(data: CreateFamilyRequest): void {
  validateFamilyName(data.name)
}

/**
 * Valida el nombre de la familia
 */
export function validateFamilyName(name: string): void {
  if (!name || name.trim().length === 0) {
    throw new Error('El nombre de la familia es requerido')
  }
  if (name.trim().length < 3) {
    throw new Error('El nombre de la familia debe tener al menos 3 caracteres')
  }
  if (name.trim().length > 100) {
    throw new Error('El nombre de la familia no puede exceder 100 caracteres')
  }
}

/**
 * Valida los datos para crear un miembro
 */
export function validateMemberData(data: CreateMemberRequest): void {
  validateMemberFirstName(data.firstName)
}

/**
 * Valida el nombre del miembro
 * @deprecated Use validateMemberFirstName
 */
export function validateMemberName(name: string): void {
  validateMemberFirstName(name)
}

/**
 * Valida el primer nombre del miembro
 */
export function validateMemberFirstName(firstName: string): void {
  if (!firstName || firstName.trim().length === 0) {
    throw new Error('El nombre del miembro es requerido')
  }
  if (firstName.trim().length < 2) {
    throw new Error('El nombre del miembro debe tener al menos 2 caracteres')
  }
  if (firstName.trim().length > 50) {
    throw new Error('El nombre del miembro no puede exceder 50 caracteres')
  }
}

// ============== UTILIDADES ==============

/**
 * Obtiene el nombre completo de un miembro
 */
export function getMemberFullName(member: FamilyMember): string {
  if (member.firstName && member.lastName) {
    return `${member.firstName} ${member.lastName}`.trim()
  }
  return member.name || member.firstName || ''
}

/**
 * Ordena miembros por nombre
 */
export function sortMembersByName(members: FamilyMember[]): FamilyMember[] {
  return [...members].sort((a, b) => {
    const nameA = getMemberFullName(a)
    const nameB = getMemberFullName(b)
    return nameA.localeCompare(nameB)
  })
}

// ============== COLOR DE AVATAR ==============

/**
 * Paleta de avatares; el usuario principal usa el azul reservado para "tú".
 * No viene del backend (MemberResponse no tiene color): se calcula en cliente
 * de forma determinista para que sea estable entre pantallas (Familia, Agenda,
 * Inicio) sin duplicar la lógica en cada una.
 *
 * Son `var(--member-N)`, no hexes: el color vive en `tokens.css` (fuente única de
 * la paleta) y no en una constante de TS. Los cuatro son los pasteles de la
 * paleta Famindly, en este orden: verde, azul, rosa y amarillo. La inicial va en
 * `--on-member` (tinta oscura fija), que sobre los cuatro llega a 4,5:1 (el peor
 * par lo mide `check:tokens`). Los tokens son los mismos de día y de noche.
 *
 * Se usan como valor de `background`, así que `var()` es válido. Ninguno de
 * estos valores viaja al backend: el formulario de miembro no envía color.
 */
export const FAMILY_AVATAR_COLORS = [
  'var(--member-1)',
  'var(--member-2)',
  'var(--member-3)',
  'var(--member-4)',
]

export const CURRENT_USER_AVATAR_COLOR = 'var(--member-2)'

function hashMemberName(name: string): number {
  let h = 0
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0
  }
  return h
}

/**
 * Color estable para el avatar de un miembro. Mismo criterio en toda la app:
 * el usuario autenticado siempre en azul; el resto, por id (o hash del
 * nombre si no hay id) sobre la paleta rotativa.
 */
export function getMemberAvatarColor(member: { id?: number; name: string; isUser?: boolean }): string {
  if (member.isUser) return CURRENT_USER_AVATAR_COLOR
  const seed = member.id ?? hashMemberName(member.name)
  return FAMILY_AVATAR_COLORS[Math.abs(seed) % FAMILY_AVATAR_COLORS.length]
}

// ============== ETIQUETA DE PARENTESCO ==============

/**
 * Texto de cada `familyRole` (el `name` de `family_roles`, sembrado en V2). Los roles con género en
 * el nombre se pintan tal cual; los que no lo tienen, en forma doble, como ya hace la descripción de
 * HIJO en V2 («Hijo/a de la familia»).
 *
 * ADMIN no es un parentesco. Es el rol que recibe quien crea la familia (alta de familia, registro y
 * el relleno de V14), y ocupa la MISMA columna que el parentesco: de un ADMIN no se sabe si es padre
 * o madre. Se pinta «Administrador/a» en vez de nada porque es un dato cierto de ese miembro, y una
 * línea vacía justo en el creador de la familia parece un dato que falta.
 */
const FAMILY_ROLE_LABELS: ReadonlyMap<string, string> = new Map([
  ['ADMIN', 'Administrador/a'],
  ['PADRE', 'Padre'],
  ['MADRE', 'Madre'],
  ['HIJO', 'Hijo/a'],
  ['ABUELO', 'Abuelo'],
  ['ABUELA', 'Abuela'],
  ['TUTOR', 'Tutor/a'],
  ['OTRO', 'Otro'],
])

/**
 * Etiqueta legible del parentesco de un miembro, a partir de su `familyRole`. Es lo único que manda
 * el backend: el antiguo `relationship` del tipo escrito a mano no ha viajado nunca.
 *
 * Un rol que no esté en la tabla (uno nuevo en `family_roles`) se pinta con su nombre capitalizado
 * en vez de dejar la línea vacía otra vez. Sin rol, `''`.
 */
export function getFamilyRoleLabel(familyRole?: string | null): string {
  if (!familyRole) return ''
  const known = FAMILY_ROLE_LABELS.get(familyRole)
  if (known) return known
  const lower = familyRole.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

// ============== AUTORIZACIÓN EN LA UI (defensa en profundidad) ==============

/**
 * EP-26 (B7.7, mr-13): ¿quien mira esta lista de miembros es ADMIN de esa
 * familia? Se resuelve buscando su propio miembro por `userId` (el `id` del
 * usuario autenticado) y comprobando si administra (`isFamilyAdminMember`) —
 * no requiere ninguna llamada nueva, la lista de miembros ya lo trae.
 *
 * No se busca por `isUser`: el backend no lo manda (no está en
 * `MemberResponse`), así que esto daba siempre `false` y la interfaz no
 * ofrecía nunca las acciones de administración.
 *
 * Defensa en profundidad, no el control real: el backend
 * (`FamilyAuthorizationService.canPurgeMember`) sigue siendo quien decide.
 * Esto solo evita OFRECER en la interfaz una acción que el servidor va a
 * rechazar con 403.
 */
export function isViewerFamilyAdmin(members: FamilyMember[], viewerUserId: number | null | undefined): boolean {
  if (viewerUserId == null) return false
  const self = members.find((m) => m.userId === viewerUserId)
  return self ? isFamilyAdminMember(self) : false
}

/**
 * Parentesco para pintar en una ficha, con «· Admin» si además administra la familia. Quien solo
 * tiene el rol heredado `ADMIN` ya se lee «Administrador/a» y no lo repite.
 */
export function getMemberRoleLabel(member: Pick<FamilyMember, 'isFamilyAdmin' | 'familyRole'>): string {
  const role = getFamilyRoleLabel(member.familyRole)
  if (!isFamilyAdminMember(member) || member.familyRole === 'ADMIN') return role
  return role ? `${role} · Admin` : 'Admin'
}

/**
 * ¿Administra este miembro su familia? La administración es una marca aparte del parentesco
 * (`isFamilyAdmin`, V50); el rol heredado `ADMIN` sigue contando, igual que en el backend
 * (`FamilyMember#hasFamilyAdminRights`).
 */
export function isFamilyAdminMember(member: Pick<FamilyMember, 'isFamilyAdmin' | 'familyRole'>): boolean {
  return member.isFamilyAdmin === true || member.familyRole === 'ADMIN'
}
